import type { AppliedFilter } from '@/components/DataTable/AppliedFiltersBar'
import { DateRangeFilter, Filter, NumberRangeFilter } from '@/components/DataTable/FilterControls'
import { TriStateCheckboxListValues } from '@/components/DataTable/FilterControls/Filters/TriStateCheckboxList'
import { useIntlLocale } from '@/hooks/useIntlLocale'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { parseDateOnly } from '@/utils/DateOnlyHelpers'
import { INDETERMINATE } from '@repo/constants'
import { useNavigate } from '@tanstack/react-router'
import { useMemo } from 'react'

export const CheckboxListControl = 'CheckboxList'
export const ComboboxSearchControl = 'ComboboxSearch'
export const SingleToggleListControl = 'SingleToggleList'
export const MultipleToggleListControl = 'MultipleToggleList'
export const TriStateCheckboxListControl = 'TriStateCheckboxList'
export const TriStateCheckboxControl = 'TriStateCheckbox'
export const DateRangeControl = 'DateRange'
export const MonthYearControl = 'MonthYear'
export const NumberRangeControl = 'NumberRange'
export const TextInputControl = 'TextInput'

export type FilterControl =
  | typeof CheckboxListControl
  | typeof ComboboxSearchControl
  | typeof SingleToggleListControl
  | typeof MultipleToggleListControl
  | typeof TriStateCheckboxListControl
  | typeof TriStateCheckboxControl
  | typeof DateRangeControl
  | typeof MonthYearControl
  | typeof NumberRangeControl
  | typeof TextInputControl

export type FilterSelectionMode = 'single' | 'multiple'

/**
 * The product dimensions every CRT page shows first, in the order readers scan them. Any filter not
 * listed keeps its page's own relative order after these. `brandCode` rides beside `brand`.
 */
export const FILTER_DISPLAY_ORDER = [
  'productCategory',
  'materialGroupCode',
  'brand',
  'brandCode',
  'variant',
  'productType',
  'marking',
  'packContentSize',
] as const

const filterDisplayRank = (name: string) => {
  const rank = (FILTER_DISPLAY_ORDER as readonly string[]).indexOf(name)
  return rank === -1 ? FILTER_DISPLAY_ORDER.length : rank
}

/** Stable: ranked names first in `FILTER_DISPLAY_ORDER`, the rest untouched behind them. */
export function sortFiltersByDisplayOrder<T extends { name: string }>(filters: T[]): T[] {
  return filters
    .map((filter, index) => ({ filter, index }))
    .sort(
      (a, b) =>
        filterDisplayRank(a.filter.name) - filterDisplayRank(b.filter.name) || a.index - b.index,
    )
    .map(({ filter }) => filter)
}

export function removeEmptyFilters(filters: unknown) {
  return filters
    ? Object.fromEntries(
        Object.entries(filters).filter(([, value]) => {
          return (
            value !== '' &&
            value !== undefined &&
            (!Array.isArray(value) || value.length > 0) &&
            value !== INDETERMINATE
          )
        }),
      )
    : {}
}

function isDateRangeFilter(filter: Filter<unknown>): filter is DateRangeFilter<unknown> {
  return filter.control === DateRangeControl
}

function isNumberRangeFilter(filter: Filter<unknown>): filter is NumberRangeFilter<unknown> {
  return filter.control === NumberRangeControl
}

function isMonthYearFilter(filter: Filter<unknown>): boolean {
  return filter.control === MonthYearControl
}

function isTriStateCheckboxListFilter(filter: Filter<unknown>): boolean {
  return filter.control === TriStateCheckboxListControl
}

/**
 * The search params a filter owns. Most filters are one control to one param, but a range spans two
 * and a tri-state list writes one key per option — so removing a filter means removing a set of keys,
 * not one.
 */
export function filterParamKeys(filter: Filter<unknown>): string[] {
  if (isDateRangeFilter(filter)) return [filter.fromParamName, filter.toParamName]
  if (isNumberRangeFilter(filter)) return [filter.minParamName, filter.maxParamName]
  if (isTriStateCheckboxListFilter(filter)) return filter.options?.map((opt) => opt.value) ?? []

  return [filter.name]
}

/**
 * The value a filter's panel control holds when nothing is selected — the same value each control's
 * own reset link produces. Clearing a filter has to write this back through `onChange`, because the
 * panel keeps its own state: leave it and the next "Show results" re-applies what was just removed.
 */
export function clearedFilterValue(filter: Filter<unknown>): unknown {
  switch (filter.control) {
    case CheckboxListControl:
      return new Set<string>()

    case ComboboxSearchControl:
    case MultipleToggleListControl:
      return []

    case TextInputControl:
      return ''

    case DateRangeControl:
      return { from: undefined, to: undefined }

    case NumberRangeControl:
      return { min: undefined, max: undefined }

    case TriStateCheckboxListControl:
      return ((filter.value as TriStateCheckboxListValues) ?? []).map((option) => ({
        ...option,
        checked: undefined,
      }))

    // SingleToggleList, MonthYear and TriStateCheckbox all hold a single optional value.
    default:
      return undefined
  }
}

// The generic navigate has no route to type itself against here; the hook only ever edits search
// params, so it is narrowed to that, the same way `useTablePagination` does.
type SearchNavigate = (options: { search: Record<string, unknown> }) => void

type BadgeDateFormatters = Pick<
  ReturnType<typeof useLocalizedDate>,
  'formatDateLong' | 'formatMonthYear'
>

/** A date bound as a search holds it: a Date once a route schema coerced it, or the raw 'YYYY-MM-DD'. */
const toBoundDate = (value: unknown): Date | undefined =>
  value instanceof Date ? value : typeof value === 'string' ? parseDateOnly(value) : undefined

/** A number-range bound written the way its field writes it: amounts with the symbol and separators. */
function formatRangeBound(
  value: unknown,
  format: NumberRangeFilter<unknown>['format'],
  intlLocale: string | undefined,
): string {
  if (typeof value !== 'number' || !intlLocale) return String(value)
  if (format === 'currency') {
    return new Intl.NumberFormat(intlLocale, {
      style: 'currency',
      currency: 'CAD',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value)
  }
  if (format === 'decimal')
    return new Intl.NumberFormat(intlLocale, { maximumFractionDigits: 2 }).format(value)
  return new Intl.NumberFormat(intlLocale, { useGrouping: false }).format(value)
}

/**
 * One badge per filter the search holds a value for, in panel order. Pure, so it serves both the
 * URL-held tables (`useAppliedFilters`, which adds the clear action) and a page whose applied
 * filters are local state and so has nothing to navigate to. `intlLocale` formats number-range
 * bounds; without it they print as typed.
 */
export function appliedFilterBadges<T>(
  filters: Filter<unknown>[],
  search: T,
  dates: BadgeDateFormatters,
  intlLocale?: string,
): AppliedFilter[] {
  const { formatDateLong, formatMonthYear } = dates

  // Same order as the panel, so a chip sits where its control does.
  const applied = sortFiltersByDisplayOrder(filters).filter((f) => {
    if (isDateRangeFilter(f)) {
      const fromKey = f.fromParamName as keyof T
      const toKey = f.toParamName as keyof T
      const fromValue = search[fromKey]
      const toValue = search[toKey]

      return fromValue !== undefined || toValue !== undefined
    }

    if (isNumberRangeFilter(f)) {
      const minValue = search[f.minParamName as keyof T]
      const maxValue = search[f.maxParamName as keyof T]

      return minValue !== undefined || maxValue !== undefined
    }

    // TriStateCheckboxList stores values as individual keys (e.g., isOrder, isStrategic)
    // Check if any of the options have a value set in the search params
    if (isTriStateCheckboxListFilter(f)) {
      return f.options?.some((opt) => {
        const value = search[opt.value as keyof T]
        return value !== undefined
      })
    }

    const value = search[f.name as keyof T]

    return !(
      value === undefined ||
      value === '' ||
      (Array.isArray(value) && value.length === 0) ||
      (value instanceof Set && value.size === 0)
    )
  })

  const badgeFor = (f: Filter<unknown>) => {
    if (isDateRangeFilter(f)) {
      const fromKey = f.fromParamName as keyof T
      const toKey = f.toParamName as keyof T
      const fromValue = search[fromKey]
      const toValue = search[toKey]

      const fromDate = toBoundDate(fromValue)
      const toDate = toBoundDate(toValue)
      const from = fromDate ? formatDateLong(fromDate) : undefined
      const to = toDate ? formatDateLong(toDate) : undefined

      return {
        name: f.title,
        value: from && to ? `${from} - ${to}` : from ? `≥ ${from}` : to ? `≤ ${to}` : '',
      }
    }

    if (isNumberRangeFilter(f)) {
      const minValue = search[f.minParamName as keyof T]
      const maxValue = search[f.maxParamName as keyof T]

      const parts = []
      if (minValue !== undefined)
        parts.push(`≥ ${formatRangeBound(minValue, f.format, intlLocale)}`)
      if (maxValue !== undefined)
        parts.push(`≤ ${formatRangeBound(maxValue, f.format, intlLocale)}`)

      return {
        name: f.title,
        value: parts.join(' · '),
      }
    }

    // TriStateCheckboxList: collect all applied flag values
    if (isTriStateCheckboxListFilter(f)) {
      const appliedFlags =
        f.options
          ?.filter((opt) => {
            const value = search[opt.value as keyof T]
            return value !== undefined
          })
          .map((opt) => {
            const value = search[opt.value as keyof T]
            const label = opt.label
            return value === true ? label : `${label} (off)`
          }) ?? []

      return {
        name: f.title,
        value: appliedFlags,
      }
    }

    // A month filter holds a single month-start value — a Date once coerced by the route
    // schema, or the raw `YYYY-MM-01` string. Formatted as a month in the badge.
    if (isMonthYearFilter(f)) {
      const month = toBoundDate(search[f.name as keyof T])

      return { name: f.title, value: month ? formatMonthYear(month) : '' }
    }

    const value = search[f.name as keyof T]!
    const values = value instanceof Set ? Array.from(value) : value

    return {
      name: f.title,
      value: Array.isArray(values)
        ? values.map((val) => f.options?.find((opt) => opt.value === val)?.label || val)
        : f.options?.find((opt) => opt.value === values)?.label || values,
    }
  }

  return applied.map((f) => ({ ...badgeFor(f), key: f.name }))
}

export function useAppliedFilters<T>(filters: Filter<unknown>[], search: T) {
  const dates = useLocalizedDate()
  const intlLocale = useIntlLocale()
  const navigate = useNavigate() as unknown as SearchNavigate

  // Drops a filter in one click from its badge. The panel's own state and the URL both hold the
  // selection, so both have to go: the URL is what the query reads, the panel state is what the next
  // "Show results" would send. The page's `onShowResults` cannot do this — it reads the page's filter
  // state from the render closure, which is still the old value in the same tick as `onChange`.
  const clearFilter = (filter: Filter<unknown>) => {
    filter.onChange(clearedFilterValue(filter) as never)

    const cleared = Object.fromEntries(filterParamKeys(filter).map((key) => [key, undefined]))
    const searchParams = search as Record<string, unknown>

    navigate({
      search: {
        ...searchParams,
        ...cleared,
        // Only routes that paginate through the URL carry `page`; adding it elsewhere would invent a
        // search param the route's schema does not know.
        ...('page' in searchParams ? { page: 1 } : {}),
      },
    })
  }

  return appliedFilterBadges(filters, search, dates, intlLocale).map((badge) => ({
    ...badge,
    onClear: () => clearFilter(filters.find((f) => f.name === badge.key)!),
  }))
}

export function useAreFiltersApplied(filters: Record<string, unknown>) {
  return useMemo(() => Object.values(filters).some((value) => value !== undefined), [filters])
}

export function useSelectedFilters(filters: Record<string, unknown>) {
  return useMemo(() => removeEmptyFilters(filters), [filters])
}

/**
 * Creates an object with all filter keys set to undefined. Useful for clearing filters
 */
export function getClearedFilters(filters: Record<string, unknown>) {
  return Object.keys(filters).reduce(
    (acc, key) => ({ ...acc, [key]: undefined }),
    {} as Record<string, undefined>,
  )
}

// Reset filter values to match current URL query parameters
export function resetFiltersPanel<TValue>(
  filters: Filter<TValue>[],
  queryParams: Record<string, unknown>,
) {
  if (filters) {
    filters.forEach((filter) => {
      const queryValue = queryParams?.[filter.name]

      switch (filter.control) {
        case SingleToggleListControl:
        case TriStateCheckboxControl:
          filter.onChange(queryValue as TValue)
          break

        /**
         * A multi-toggle holds an ARRAY — that is what `clearedFilterValue` returns for it, and
         * every page reads its value with `.length`.
         *
         * It used to sit with the two single-value controls above, which legitimately hold
         * `undefined` when empty. So opening the panel while that filter was absent from the URL
         * reset it to `undefined`, and the next "Show results" threw reading `.length` of it. The
         * other two array controls below already guard the same way.
         */
        case MultipleToggleListControl:
          filter.onChange((Array.isArray(queryValue) ? queryValue : []) as TValue)
          break

        case TextInputControl:
          filter.onChange((typeof queryValue === 'string' ? queryValue : '') as TValue)
          break

        case CheckboxListControl: {
          const checkboxValues = Array.isArray(queryValue) ? queryValue : []
          filter.onChange(new Set(checkboxValues) as TValue)
          break
        }

        case ComboboxSearchControl: {
          const comboboxValues = Array.isArray(queryValue) ? queryValue : []
          filter.onChange(comboboxValues as TValue)
          break
        }

        case TriStateCheckboxListControl:
          if (Array.isArray(filter.value)) {
            const updatedValues = filter.value.map((v: { name: string; checked: unknown }) => ({
              ...v,
              checked: queryParams[v.name],
            }))
            filter.onChange(updatedValues as TValue)
          }
          break
      }
    })
  }
}
